import type { AssistantTask, TaskAutomation } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { createContext, useContext } from 'react';
import { isDesktopPreviewPath } from '@/lib/assistants/directory';
import { previewAssistantConversationApi } from './assistant-preview-data';

/** A saved agent the assistant hands work to, as its views show it: its avatar, when it has one. */
export type AssistantMemberView = {
  agentId: string;
  name: string;
  agent: string | null;
  avatar?: string;
};

/**
 * What the assistant's panel reads besides the directory: its repositories, who made it, the
 * user's automations a task may run (among which it runs those it names).
 */
export type AssistantOverview = {
  repositories: { id: string; name: string }[];
  /** Absent from older servers. */
  automations?: TaskAutomation[];
  /** The reader created it. */
  yours: boolean;
  /** Who created it: the one, with the workspace's admins, who changes it. */
  owner: string | null;
};

/**
 * The assistant's HTTP side (`/api/assistants/:id/conversation`, `/tasks`): through this interface
 * so previews and tests can swap it.
 */
export interface AssistantConversationApi {
  overview(assistantId: string): Promise<AssistantOverview>;
  tasks(assistantId: string): Promise<AssistantTask[]>;
}

const path = (assistantId: string, rest: string) =>
  `/api/assistants/${encodeURIComponent(assistantId)}/${rest}`;

export const httpAssistantConversationApi: AssistantConversationApi = {
  overview: (assistantId) => apiFetch<AssistantOverview>(path(assistantId, 'conversation')),
  tasks: async (assistantId) =>
    (await apiFetch<{ tasks: AssistantTask[] }>(path(assistantId, 'tasks'))).tasks,
};

let previewApi: AssistantConversationApi | null = null;

/** The server's, or the boards' on `/desktop-preview…`. */
export function defaultAssistantConversationApi(): AssistantConversationApi {
  if (!isDesktopPreviewPath()) return httpAssistantConversationApi;
  previewApi ??= previewAssistantConversationApi();
  return previewApi;
}

export const AssistantConversationApiContext = createContext<AssistantConversationApi | null>(null);

export function useAssistantConversationApi(): AssistantConversationApi {
  return useContext(AssistantConversationApiContext) ?? defaultAssistantConversationApi();
}
